<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import {
  normalizeAgentPhone,
  formatAgentPhone,
} from 'dashboard/helper/agentPhoneNumber';
import Avatar from 'next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import AgentFormDropdown from './AgentFormDropdown.vue';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  submitLabel: {
    type: String,
    required: true,
  },
  submitIcon: {
    type: String,
    default: 'i-lucide-user-round-plus',
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
  standalone: {
    type: Boolean,
    default: false,
  },
  mobileLayout: {
    type: Boolean,
    default: false,
  },
  centerMobileActions: {
    type: Boolean,
    default: false,
  },
  unifiedMobileActions: {
    type: Boolean,
    default: false,
  },
  emailEditable: {
    type: Boolean,
    default: true,
  },
  emailVerified: {
    type: Boolean,
    default: null,
  },
  showInvitationNotice: {
    type: Boolean,
    default: false,
  },
  initialValue: {
    type: Object,
    default: () => ({}),
  },
  avatarUrl: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close', 'submit']);

const store = useStore();
const { t } = useI18n();
const isRTL = useMapGetter('accounts/isRTL');

const agentName = ref('');
const agentEmail = ref('');
const agentPhone = ref('');
const agentDisplayName = ref('');
const agentAvailability = ref('online');
const selectedRoleId = ref('agent');
const teamRoles = ref({});

const rules = {
  agentName: { required },
  agentEmail: { required, email },
  agentPhone: { validPhone: value => normalizeAgentPhone(value) !== null },
  selectedRoleId: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  agentEmail,
  agentPhone,
  selectedRoleId,
});

const teamUIFlags = useMapGetter('teams/getUIFlags');
const teams = useMapGetter('teams/getTeams');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const sortedTeams = computed(() =>
  [...teams.value].sort((firstTeam, secondTeam) =>
    String(firstTeam.name || '').localeCompare(String(secondTeam.name || ''))
  )
);

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
    systemKey: role.system_key,
  }));

  return customRoles.some(role => role.systemKey)
    ? [
        ...defaultRoles.filter(role => role.id === props.initialValue?.roleId),
        ...customRoles,
      ]
    : [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      String(role.id) === String(selectedRoleId.value) ||
      role.name === selectedRoleId.value
  )
);

const roleOptions = computed(() =>
  roles.value.map(role => ({ value: role.id, label: role.label }))
);

const previewName = computed(
  () =>
    agentDisplayName.value ||
    agentName.value ||
    t('AGENT_MGMT.FORM.PREVIEW_NAME')
);

const selectedTeamCount = computed(
  () => Object.values(teamRoles.value).filter(Boolean).length
);

const teamAssignments = computed(() =>
  Object.entries(teamRoles.value)
    .filter(([, role]) => role)
    .map(([teamId, role]) => ({ team_id: Number(teamId), role }))
);

const isTeamRoleEnabled = (teamId, role) => teamRoles.value[teamId] === role;

const toggleTeamRole = (teamId, role, enabled) => {
  teamRoles.value = {
    ...teamRoles.value,
    [teamId]: enabled ? role : null,
  };
};

const onPhoneInput = event => {
  agentPhone.value = formatAgentPhone(event.target.value);
  event.target.value = agentPhone.value;
  v$.value.agentPhone.$touch();
};

const resetForm = value => {
  agentName.value = value?.name || '';
  agentEmail.value = value?.email || '';
  agentPhone.value = formatAgentPhone(value?.phone || '');
  agentDisplayName.value = value?.displayName || '';
  agentAvailability.value = value?.availability || 'online';
  selectedRoleId.value =
    value?.roleId ??
    getCustomRoles.value.find(role => role.system_key === 'vendedor')?.id ??
    'agent';
  teamRoles.value = { ...(value?.teamRoles || {}) };
  v$.value.$reset();
  if (agentPhone.value && normalizeAgentPhone(agentPhone.value) === null) {
    v$.value.agentPhone.$touch();
  }
};

const close = () => {
  if (!props.isSubmitting) emit('close');
};

const closeFromBackdrop = () => {
  if (!props.standalone) close();
};

const onKeydown = event => {
  if (event.key === 'Escape' && !props.isSubmitting) close();
};

// Snapshot: so reinicializa quando o objeto de entrada troca de identidade, para
// uma atualizacao de presenca vinda do websocket nao apagar o que foi digitado.
watch(() => props.initialValue, resetForm, { immediate: true });
watch(getCustomRoles, profiles => {
  if (!props.initialValue?.roleId && selectedRoleId.value === 'agent') {
    selectedRoleId.value =
      profiles.find(role => role.system_key === 'vendedor')?.id ?? 'agent';
  }
});

onMounted(() => {
  document.addEventListener('keydown', onKeydown);
  // Os dois ja vem pre-carregados pela guarda da rota; so busca o que faltar.
  if (!teams.value.length) {
    store
      .dispatch('teams/get')
      .catch(() => useAlert(t('AGENT_MGMT.FORM.TEAMS.LOAD_ERROR')));
  }
  if (!getCustomRoles.value.length) {
    store.dispatch('customRole/getCustomRole').catch(() => {});
  }
});

onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));

const onSubmit = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const usesCustomRole = Boolean(
    selectedRole.value?.name?.startsWith('custom_')
  );

  const profileUnchanged =
    String(selectedRole.value.id) === String(props.initialValue?.roleId);
  let nativeRole = selectedRole.value.name;
  if (usesCustomRole) {
    if (profileUnchanged && props.initialValue?.nativeRole) {
      nativeRole = props.initialValue.nativeRole;
    } else if (selectedRole.value.systemKey === 'vendedor') {
      nativeRole = 'agent';
    } else {
      nativeRole = ['proprietario', 'gestor'].includes(
        selectedRole.value.systemKey
      )
        ? 'administrator'
        : props.initialValue?.nativeRole || 'agent';
    }
  }

  emit('submit', {
    name: agentName.value.trim(),
    email: agentEmail.value.trim(),
    phone: normalizeAgentPhone(agentPhone.value),
    displayName: agentDisplayName.value.trim(),
    availability: agentAvailability.value,
    role: nativeRole,
    customRoleId: usesCustomRole ? selectedRole.value.id : null,
    teamAssignments: teamAssignments.value,
  });
};
</script>

<template>
  <Teleport to="body" :disabled="standalone">
    <div
      class="agent-form-modal flex w-full justify-center"
      :dir="isRTL ? 'rtl' : 'ltr'"
      :class="[
        standalone
          ? 'agent-form-modal--inline relative items-start'
          : 'agent-form-modal--overlay fixed inset-0 z-[9999] items-end bg-n-alpha-black2 backdrop-blur-[4px] sm:items-center sm:p-4',
      ]"
      @mousedown.self="closeFromBackdrop"
    >
      <form
        class="agent-form-modal__dialog flex w-full max-w-[960px] flex-col overflow-hidden border border-n-weak bg-n-solid-1"
        :class="[
          standalone
            ? 'rounded-lg shadow-sm'
            : 'max-h-[100dvh] shadow-xl sm:max-h-[calc(100dvh-2rem)] sm:rounded-xl',
          mobileLayout && !standalone
            ? 'max-sm:h-[100dvh] max-sm:border-x-0 max-sm:border-b-0'
            : '',
        ]"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
        @submit.prevent="onSubmit"
      >
        <header
          class="agent-form-modal__header acl-mobile-account-menu-host flex flex-shrink-0 items-start justify-between gap-4 border-b border-n-weak px-6 py-5 sm:px-8 sm:py-6"
          :class="mobileLayout ? 'max-sm:px-4 max-sm:py-4' : ''"
        >
          <div class="agent-form-modal__header-text min-w-0">
            <h2
              class="agent-form-modal__title m-0 text-xl font-semibold text-n-slate-12"
            >
              {{ title }}
            </h2>
            <p
              v-if="description"
              class="agent-form-modal__subtitle mb-0 mt-1 text-sm leading-5 text-n-slate-11"
            >
              {{ description }}
            </p>
          </div>
          <Button
            v-if="!standalone"
            ghost
            slate
            type="button"
            icon="i-lucide-x"
            class="agent-form-modal__close"
            :disabled="isSubmitting"
            :aria-label="$t('AGENT_MGMT.FORM.CANCEL')"
            @click="close"
          />
        </header>

        <div
          class="agent-form-modal__body scrollbar-thin flex flex-1 flex-col"
          :class="standalone ? 'overflow-visible' : 'min-h-0 overflow-y-auto'"
        >
          <section
            class="agent-form-modal__identity flex flex-col border-b border-n-weak px-6 py-7 sm:px-8"
            :class="mobileLayout ? 'max-sm:px-4 max-sm:py-5' : ''"
          >
            <div
              class="agent-form-modal__preview mb-6 flex items-center gap-4"
              :class="mobileLayout ? 'max-sm:mb-5 max-sm:gap-3' : ''"
            >
              <Avatar
                class="agent-form-modal__preview-avatar flex-shrink-0"
                :class="
                  mobileLayout
                    ? 'max-sm:!size-14 max-sm:[&>span]:!size-14 max-sm:[&>span]:!rounded-full'
                    : ''
                "
                rounded-full
                :src="avatarUrl"
                :name="previewName"
                :size="80"
              />
              <div class="agent-form-modal__preview-text min-w-0">
                <span
                  class="agent-form-modal__preview-name block truncate text-base font-semibold text-n-slate-12"
                >
                  {{ previewName }}
                </span>
                <span
                  class="agent-form-modal__preview-role mt-1 inline-flex rounded-full bg-n-slate-3 px-3 py-1 text-xs font-medium text-n-slate-11"
                >
                  {{ selectedRole?.label }}
                </span>
              </div>
            </div>

            <div
              class="agent-form-modal__fields grid gap-x-6 gap-y-5 sm:grid-cols-2"
              :class="mobileLayout ? 'max-sm:gap-y-4' : ''"
            >
              <label
                class="agent-form-modal__field agent-form-modal__field--name m-0 block min-w-0 text-sm font-medium text-n-slate-12"
                :class="{ error: v$.agentName.$error }"
              >
                {{ $t('AGENT_MGMT.FORM.NAME.LABEL') }}
                <input
                  v-model="agentName"
                  type="text"
                  autocomplete="name"
                  class="agent-form-modal__input reset-base mt-2 h-11 w-full rounded-lg border border-solid border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none transition-colors placeholder:text-n-slate-9 focus:border-n-brand"
                  :placeholder="$t('AGENT_MGMT.FORM.NAME.PLACEHOLDER')"
                  @input="v$.agentName.$touch"
                />
                <span
                  v-if="v$.agentName.$error"
                  class="agent-form-modal__error message"
                >
                  {{ $t('AGENT_MGMT.FORM.NAME.ERROR') }}
                </span>
              </label>

              <label
                class="agent-form-modal__field agent-form-modal__field--email m-0 block min-w-0 text-sm font-medium text-n-slate-12"
                :class="{ error: v$.agentEmail.$error }"
              >
                {{ $t('AGENT_MGMT.FORM.EMAIL.LABEL') }}
                <input
                  v-model="agentEmail"
                  type="email"
                  autocomplete="email"
                  :readonly="!emailEditable"
                  class="agent-form-modal__input reset-base mt-2 h-11 w-full rounded-lg border border-solid border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none transition-colors placeholder:text-n-slate-9 focus:border-n-brand read-only:cursor-not-allowed read-only:bg-n-slate-2 read-only:text-n-slate-11"
                  :placeholder="$t('AGENT_MGMT.FORM.EMAIL.PLACEHOLDER')"
                  @input="v$.agentEmail.$touch"
                />
                <span
                  v-if="v$.agentEmail.$error"
                  class="agent-form-modal__error message"
                >
                  {{ $t('AGENT_MGMT.FORM.EMAIL.ERROR') }}
                </span>
                <span
                  v-else-if="!emailEditable"
                  class="agent-form-modal__hint mt-1.5 block text-xs font-normal leading-4 text-n-slate-11"
                >
                  {{ $t('AGENT_MGMT.FORM.EMAIL.READONLY_HINT') }}
                </span>
                <span
                  v-if="!emailEditable && emailVerified === true"
                  class="mt-1.5 block text-xs font-normal leading-4 text-n-slate-11"
                >
                  {{ $t('AGENT_MGMT.LIST.VERIFIED') }}
                </span>
                <span
                  v-if="!emailEditable && emailVerified === false"
                  class="mt-1.5 block text-xs font-normal leading-4 text-n-slate-11"
                >
                  {{ $t('AGENT_MGMT.LIST.VERIFICATION_PENDING') }}
                  <slot name="email-verification" />
                </span>
              </label>

              <label
                class="agent-form-modal__field agent-form-modal__field--phone m-0 block min-w-0 text-sm font-medium text-n-slate-12"
              >
                {{ $t('AGENT_MGMT.FORM.PHONE.LABEL') }}
                <input
                  :value="agentPhone"
                  @input="onPhoneInput"
                  type="tel"
                  inputmode="tel"
                  maxlength="40"
                  :aria-invalid="v$.agentPhone.$error"
                  aria-describedby="agent-phone-hint"
                  autocomplete="tel"
                  class="agent-form-modal__input reset-base mt-2 h-11 w-full rounded-lg border border-solid border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none transition-colors placeholder:text-n-slate-9 focus:border-n-brand"
                  :placeholder="$t('AGENT_MGMT.FORM.PHONE.PLACEHOLDER')"
                />
                <span
                  id="agent-phone-hint"
                  class="mt-1.5 block text-xs font-normal leading-4"
                  :class="
                    v$.agentPhone.$error ? 'text-n-ruby-9' : 'text-n-slate-11'
                  "
                >
                  {{
                    $t(
                      v$.agentPhone.$error
                        ? 'CONTACT_FORM.FORM.PHONE_NUMBER.ERROR'
                        : 'CONTACT_FORM.FORM.PHONE_NUMBER.HELP'
                    )
                  }}
                </span>
              </label>

              <label
                class="agent-form-modal__field agent-form-modal__field--display-name m-0 block min-w-0 text-sm font-medium text-n-slate-12"
              >
                {{ $t('AGENT_MGMT.FORM.DISPLAY_NAME.LABEL') }}
                <input
                  v-model="agentDisplayName"
                  type="text"
                  class="agent-form-modal__input reset-base mt-2 h-11 w-full rounded-lg border border-solid border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none transition-colors placeholder:text-n-slate-9 focus:border-n-brand"
                  :placeholder="$t('AGENT_MGMT.FORM.DISPLAY_NAME.PLACEHOLDER')"
                />
              </label>

              <div
                class="agent-form-modal__field agent-form-modal__field--role m-0 block min-w-0"
                :class="{ error: v$.selectedRoleId.$error }"
              >
                <span
                  class="agent-form-modal__label block text-sm font-medium text-n-slate-12"
                >
                  {{ $t('AGENT_MGMT.FORM.AGENT_TYPE.LABEL') }}
                </span>
                <AgentFormDropdown
                  v-model="selectedRoleId"
                  class="agent-form-modal__dropdown agent-form-modal__dropdown--role mt-2"
                  :options="roleOptions"
                  :aria-label="$t('AGENT_MGMT.FORM.AGENT_TYPE.LABEL')"
                  :search-placeholder="$t('AGENT_MGMT.FORM.SEARCH_PLACEHOLDER')"
                  :empty-text="$t('AGENT_MGMT.FORM.NO_RESULTS')"
                  :has-error="v$.selectedRoleId.$error"
                  @update:model-value="v$.selectedRoleId.$touch"
                />
              </div>
            </div>

            <div
              v-if="showInvitationNotice"
              class="agent-form-modal__notice mt-6 flex items-start gap-3 border-t border-n-weak pt-5 text-sm text-n-slate-11"
            >
              <span
                class="agent-form-modal__notice-icon i-lucide-mail-check mt-0.5 size-4 flex-shrink-0 text-n-brand"
              />
              <p class="agent-form-modal__notice-text m-0 leading-5">
                {{ $t('AGENT_MGMT.FORM.INVITATION_NOTICE') }}
              </p>
            </div>
          </section>

          <section
            class="agent-form-modal__teams flex flex-col px-6 py-7 sm:px-8"
            :class="mobileLayout ? 'max-sm:px-4 max-sm:py-5' : ''"
          >
            <div
              class="agent-form-modal__teams-header flex items-center justify-between gap-3"
              :class="mobileLayout ? 'max-sm:flex-col max-sm:items-start' : ''"
            >
              <div class="agent-form-modal__teams-heading">
                <h3
                  class="agent-form-modal__teams-title m-0 text-base font-semibold text-n-slate-12"
                >
                  {{ $t('AGENT_MGMT.FORM.TEAMS.TITLE') }}
                </h3>
                <p
                  class="agent-form-modal__teams-description mb-0 mt-1 text-sm leading-5 text-n-slate-11"
                >
                  {{ $t('AGENT_MGMT.FORM.TEAMS.DESCRIPTION') }}
                </p>
              </div>
              <span
                class="agent-form-modal__teams-count flex-shrink-0 rounded-full bg-n-slate-3 px-2.5 py-1 text-xs font-medium text-n-slate-11"
              >
                {{
                  $t('AGENT_MGMT.FORM.TEAMS.SELECTED_COUNT', {
                    count: selectedTeamCount,
                  })
                }}
              </span>
            </div>

            <div
              v-if="teamUIFlags.isFetching"
              class="agent-form-modal__teams-loading flex min-h-40 items-center justify-center"
            >
              <Spinner />
            </div>

            <div
              v-else-if="sortedTeams.length"
              class="agent-form-modal__teams-groups mt-6 flex flex-col gap-5"
              :class="mobileLayout ? 'max-sm:mt-4 max-sm:gap-4' : ''"
            >
              <section
                class="agent-form-modal__teams-group agent-form-modal__teams-group--user overflow-hidden rounded-lg border border-n-weak"
              >
                <div
                  class="agent-form-modal__teams-group-header border-b border-n-weak bg-n-solid-1 px-4 py-3"
                >
                  <h4
                    class="agent-form-modal__teams-group-title m-0 text-base font-semibold text-n-slate-12"
                  >
                    {{ $t('AGENT_MGMT.FORM.TEAMS.USER.TITLE') }}
                  </h4>
                  <p
                    class="agent-form-modal__teams-group-description mb-0 mt-1 text-xs leading-5 text-n-slate-11"
                  >
                    {{ $t('AGENT_MGMT.FORM.TEAMS.USER.DESCRIPTION') }}
                  </p>
                </div>
                <div
                  class="agent-form-modal__teams-list divide-y divide-n-weak px-4"
                >
                  <div
                    v-for="team in sortedTeams"
                    :key="`user-${team.id}`"
                    class="agent-form-modal__teams-row flex min-h-10 items-center justify-between gap-3 py-2"
                  >
                    <span
                      class="agent-form-modal__teams-name min-w-0 break-words text-sm text-n-slate-12"
                    >
                      {{ team.name }}
                    </span>
                    <ToggleSwitch
                      class="agent-form-modal__teams-toggle"
                      :model-value="isTeamRoleEnabled(team.id, 'user')"
                      :aria-label="
                        $t('AGENT_MGMT.FORM.TEAMS.USER.TOGGLE', {
                          team: team.name,
                        })
                      "
                      @update:model-value="
                        toggleTeamRole(team.id, 'user', $event)
                      "
                    />
                  </div>
                </div>
              </section>

              <section
                class="agent-form-modal__teams-group agent-form-modal__teams-group--supervisor overflow-hidden rounded-lg border border-n-weak"
              >
                <div
                  class="agent-form-modal__teams-group-header border-b border-n-weak bg-n-solid-1 px-4 py-3"
                >
                  <h4
                    class="agent-form-modal__teams-group-title m-0 text-base font-semibold text-n-slate-12"
                  >
                    {{ $t('AGENT_MGMT.FORM.TEAMS.SUPERVISOR.TITLE') }}
                  </h4>
                  <p
                    class="agent-form-modal__teams-group-description mb-0 mt-1 text-xs leading-5 text-n-slate-11"
                  >
                    {{ $t('AGENT_MGMT.FORM.TEAMS.SUPERVISOR.DESCRIPTION') }}
                  </p>
                </div>
                <div
                  class="agent-form-modal__teams-list divide-y divide-n-weak px-4"
                >
                  <div
                    v-for="team in sortedTeams"
                    :key="`supervisor-${team.id}`"
                    class="agent-form-modal__teams-row flex min-h-10 items-center justify-between gap-3 py-2"
                  >
                    <span
                      class="agent-form-modal__teams-name min-w-0 break-words text-sm text-n-slate-12"
                    >
                      {{ team.name }}
                    </span>
                    <ToggleSwitch
                      class="agent-form-modal__teams-toggle"
                      :model-value="isTeamRoleEnabled(team.id, 'supervisor')"
                      :aria-label="
                        $t('AGENT_MGMT.FORM.TEAMS.SUPERVISOR.TOGGLE', {
                          team: team.name,
                        })
                      "
                      @update:model-value="
                        toggleTeamRole(team.id, 'supervisor', $event)
                      "
                    />
                  </div>
                </div>
              </section>
            </div>

            <p
              v-else
              class="agent-form-modal__teams-empty mb-0 mt-6 text-sm text-n-slate-11"
            >
              {{ $t('AGENT_MGMT.FORM.TEAMS.EMPTY') }}
            </p>
          </section>

          <slot name="sections" />
        </div>

        <footer
          class="agent-form-modal__footer flex flex-shrink-0 items-center justify-between gap-3 border-t border-n-weak bg-n-solid-1 px-6 py-5 sm:px-8"
          :class="
            mobileLayout && !standalone
              ? 'max-sm:flex-col max-sm:items-stretch max-sm:px-4 max-sm:pb-[max(1rem,env(safe-area-inset-bottom))] max-sm:pt-3'
              : ''
          "
        >
          <div
            class="agent-form-modal__footer-start flex min-w-0 items-center gap-2"
            :class="
              mobileLayout
                ? 'max-sm:w-full max-sm:empty:hidden max-sm:[&>button]:w-full'
                : ''
            "
          >
            <slot name="footer-start" />
          </div>
          <div
            class="agent-form-modal__footer-actions flex flex-shrink-0 items-center gap-2"
            :class="
              centerMobileActions
                ? 'max-sm:w-full max-sm:justify-center'
                : unifiedMobileActions
                  ? 'max-sm:w-full max-sm:flex-col max-sm:items-stretch'
                  : mobileLayout
                    ? 'max-sm:grid max-sm:w-full max-sm:grid-cols-2'
                    : ''
            "
          >
            <Button
              faded
              slate
              type="button"
              class="agent-form-modal__cancel"
              :class="
                unifiedMobileActions
                  ? 'max-sm:w-full max-sm:bg-n-slate-9/10 max-sm:outline-n-strong max-sm:hover:bg-n-slate-9/20'
                  : mobileLayout && !centerMobileActions
                    ? 'max-sm:w-full'
                    : ''
              "
              :label="$t('AGENT_MGMT.FORM.CANCEL')"
              :disabled="isSubmitting"
              @click="close"
            />
            <Button
              type="submit"
              class="agent-form-modal__submit"
              :class="
                unifiedMobileActions
                  ? 'max-sm:w-full max-sm:bg-n-brand/10 max-sm:text-n-blue-11 max-sm:outline-n-brand max-sm:hover:bg-n-brand/20 max-sm:focus-visible:bg-n-brand/20'
                  : mobileLayout && !centerMobileActions
                    ? 'max-sm:w-full'
                    : ''
              "
              :icon="submitIcon"
              :label="submitLabel"
              :disabled="v$.$invalid || isSubmitting"
              :is-loading="isSubmitting"
            />
          </div>
        </footer>
      </form>
    </div>
  </Teleport>
</template>

<style scoped>
/* O Switch compartilhado pinta o pino com bg-n-background, que era a cor de
   fundo do modal antigo — num modal branco ele some, e contra a trilha
   desligada (slate-6) os dois estados ficam quase iguais. Aqui o pino fica
   branco e a trilha desligada escurece, para ligado/desligado se distinguirem
   nos dois temas. Restrito ao modal: o Switch e usado em varias outras telas. */
.agent-form-modal__teams-toggle:not([aria-checked='true']) {
  background-color: rgb(var(--slate-8));
}

.agent-form-modal__teams-toggle :deep(span span) {
  background-color: #fff;
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.25);
}
</style>
